import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { css } from '../../../services/css';
import { Arrow } from '../icons/arrow';
import { Load } from '../load/load';
import styles from './select.module.css';

// type SelectParams = {
//     value: string,
//     onChange: (v: string, i?: number) => void,
//     options: string[],
//     allowSearch?: boolean,
//     allowAny?: boolean,
//     disabled?: boolean,
//     type?: string,
//     [key: string]: any,
// }

export const Select = ({
    value,
    onChange,
    options,
    allowSearch = true,
    allowAny = false,
    allowEmpty = false,
    disabled = false,
    loading = false,
    onSearch = (v) => {},
    onSearchStatus = (v) => {},
    type = 'text',
    ...props
}) => {

    const [searchValue, setSearchValue] = useState(value);
    const [isSearching, setIsSearching] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [highlightIndex, setHighlightedIndex] = useState(null);
    const arrowRef = useRef(null);
    const highlightRef = useRef(null);

    const results = () => options
        .filter(o => !isSearching || (typeof o === 'string' ? o : o?.value ?? '').toLowerCase().includes((searchValue ?? '').toLowerCase()))

    useEffect(() => {
        setSearchValue((typeof value === 'string' ? value : value?.value ?? ''));
    }, [value]);

    useEffect(() => {
        if (expanded) 
            setIsSearching(false);
    }, [expanded]);


    const handleBlur = (e) => {

        //On Blur is called when the focus changes within the selector.
        //This checks to make sure the focused element is outside it.
        if (!e.currentTarget?.contains(e.relatedTarget)) {
            if (allowAny) {
                onChange(searchValue);
            } else if (searchValue === '' && allowEmpty) {
                onChange(null);
            } else {
                const option = options.find(o => (typeof o === 'string' ? o : o?.value ?? '').toLowerCase() === searchValue.toLowerCase());
                if (option === undefined)
                    setSearchValue(typeof value === 'string' ? value : value?.value ?? '');
                else
                    onChange(option);
            }

            setHighlightedIndex(null);

            setIsSearching(false);
            setExpanded(false);
        }
    }

    const handleFocus = (e) => {
        if (disabled) return;

        //Make sure the focus isn't on the arrow
        if (!arrowRef.current?.contains(e.target))
            setExpanded(true);
    }

    const handleSearch = (e) => {
        setExpanded(true);
        setIsSearching(true);
        setSearchValue(e.currentTarget.value);
    }

    const handleArrowClick = () => {
        if (disabled) return;
        setExpanded(!expanded);
    }


    const handleOptionClick = (o, i) => {
        if (disabled || (typeof o !== 'string' && o.disabled)) return;
        setIsSearching(false);
        setExpanded(false);
        onChange(o, i);
    }

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightIndex !== null) {
                let r = results()[highlightIndex];
                if (typeof r !== 'string' && r.disabled) return;

                onChange(r, highlightIndex);
            } else if (allowAny) {
                onChange(searchValue, undefined);
            } else if (searchValue === '' && allowEmpty) {
                onChange(null, undefined);
            }else {
                setSearchValue('');
            }

            setExpanded(false);
            setHighlightedIndex(null);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(((highlightIndex ?? -1) + 1) % results().length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(((highlightIndex ?? 0) - 1 + results().length) % results().length);
        }
    }

    useEffect(() => {
        onSearch(searchValue);
        onSearchStatus(isSearching);
        if (isSearching && results().length > 0) {
            setHighlightedIndex(0);
        } else {
            setHighlightedIndex(null);
        }
    }, [searchValue, isSearching]);

    useEffect(() => {
        if (highlightRef.current)
            highlightRef.current.scrollIntoView(false);
    }, [highlightIndex]);

    return <div
        tabIndex={disabled ? undefined : -1}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleInputKeyDown}
        className={css(styles.Select, expanded && styles.Expanded, disabled && styles.Disabled)}
    >
        <div className={styles.InputWrapper}>
            <input
                type={type}
                value={searchValue ?? ''}
                className={css(styles.Input, !disabled && !allowSearch && styles.NotDisabled)}
                onInput={handleSearch}
                disabled={disabled || !allowSearch}
                autoComplete={Math.random().toString()}
                {...props}
            />
        </div>

        <div
            tabIndex={-1}
            ref={arrowRef}
            className={styles.ArrowWrapper}
        >
            <button
                disabled={disabled}
                className={styles.Arrow}
                onClick={handleArrowClick}
                type='button'
            >
                {loading ?
                    <Load /> :
                    <Arrow
                        direction={expanded ? 'up' : 'down'}
                    />
                }
            </button>
        </div>

        <div className={styles.Drop}>
            {results().map((o, i) => (
                <button
                    ref={i === highlightIndex ? highlightRef : undefined}
                    type='button'
                    disabled={disabled || (typeof o !== 'string' && o.disabled)}
                    key={typeof o === 'string' ? o : o.id}
                    className={css(
                        styles.Option,
                        i === highlightIndex && styles.Highlighted,
                        (typeof o === 'string' ? o === value : o?.id === value?.id) && styles.Selected,
                    )}
                    onClick={() => handleOptionClick(o, i)}
                >{(typeof o === 'string' ? o : o.value)}</button>
            ))}
            {results().length === 0 ? <button
                    type='button'
                    disabled={disabled}
                    className={css(
                        styles.Option,
                        styles.Locked
                    )}
            >No results</button> : ''}
        </div>
    </div>
}